import produce from 'immer';
import {assertEvent, assign, setup, ActorRefFrom} from 'xstate';

import {assert} from 'Asserts';
import {FullyAccessibleGrid} from 'constants/SampleConstants';
import {allowPlateDesigns, allowPlateRecords} from 'notebook/integrations/plate-maps/FixedPlateEditorUtil';
import isConfiguration from 'notebook/util/isConfiguration';
import {Note} from 'notebook/util/NoteUtilConstants';
import {BaseStorablePaneStudyableFormGroupProps} from 'samples/BaseStorablePaneStudyableFormGroup';
import {ConcentrationLayerMachine} from 'samples/plate-maps/concentration-layers/machine/ConcentrationLayerMachine';
import {
  DisplayLayerOptionSection,
  PlateDataParameter,
  PlateMap,
  PlateMapProcedureContext,
} from 'samples/plate-maps/PlateMapConstants';
import {
  getDataParameterNamesByVersionId,
  getEmptyPlateMap,
  getInitialDataParameterTags,
  getInitialSelectedDataParameters,
} from 'samples/plate-maps/PlateMapUtil';
import {NO_MATERIAL_INPUT_SENTINEL} from 'samples/plate-maps/transfer-table/FillPlateTransferTableConstants';
import {
  filterGroupedValidation,
  filterValidation,
  getErrorsByLayerAndLevel,
  getFlattenedValidation,
} from 'samples/plate-maps/validation/PlateMapValidationUtil';
import {PlateMapErrorLevel, PlateMapValidation} from 'samples/plate-maps/validation/ValidationTypes';
import StorableStore from 'samples/StorableStore';
import {StepItemStatus} from 'ui-library/StepNav';

import {
  ANNOTATE_FILL_PLATE_MODAL_STEPS,
  InitiatingPlateDesignState,
  PlateMapType,
} from '../AnnotateFillPlateModalConstants';
import {
  AnnotateFillPlateModalReducerAction,
  AnnotateFillPlateModalReducerActionType,
  AnnotateFillPlateModalState,
  DEFAULT_ANNOTATE_FILL_PLATE_MODAL_STATE,
  DEFAULT_PLATE_DESIGN_STATE,
  resetDesignCreateExistingPlateStates,
} from './AnnotateFillPlateMachineConstants';
import {getInitialRegistryForPlateDesign} from './utils';

interface InterimRefactorContext {
  existingPlateDesignNames: Array<string>;
  isFromExistingPlateDesign: boolean;

  plateDataParameters: Array<PlateDataParameter>;

  selectedPlateDataParameterIds?: Set<string>;

  fileId: string;

  procedureContext: PlateMapProcedureContext;

  initiatingPlateDesignState?: InitiatingPlateDesignState | null;

  allowPlateDesigns: boolean;
  allowPlateRecords: boolean;

  studyableProps: {
    initialStudyIds: Array<string>;
    studySearchInputProps: BaseStorablePaneStudyableFormGroupProps['studySearchInputProps'];
  };

  note?: Note;

  disabledSections: Array<DisplayLayerOptionSection>;

  /** Actor reference for the concentration layer machine */
  concentrationLayerMachine?: ActorRefFrom<typeof ConcentrationLayerMachine>;
}

type AnnotateFillPlateModalContext = AnnotateFillPlateModalState & InterimRefactorContext;

type InterimRefactorEvents =
  | {type: 'next'}
  | {type: 'back'}
  | {type: 'updateProcedureContext'; procedureContext: PlateMapProcedureContext};

type AnnotateFillPlateModalEvents = AnnotateFillPlateModalReducerAction | InterimRefactorEvents;
export interface AnnotateFillPlateModalInput {
  initialState?: AnnotateFillPlateModalState;
  existingPlateDesignNames: Array<string>;
  initiatingPlateDesignState?: InitiatingPlateDesignState | null;
  initialStudyIds?: Array<string> | null;
  procedureContext: PlateMapProcedureContext;
  fileId: string;

  note?: Note;

  studyableProps: {
    initialStudyIds: Array<string>;
    studySearchInputProps: BaseStorablePaneStudyableFormGroupProps['studySearchInputProps'];
  };
}

/**
 * Finite State Machine for the Annotate Fill Plate Modal
 * Currently this is just acting like a simple reducer as the machine
 * only has one state with no events or transitions.
 *
 * Further migration into a full fledged state machine is coming soon.
 * https://benchling.atlassian.net/wiki/spaces/IR/pages/1241546866/Concentration+Layers+for+Plate+Maps
 *
 * Reach out to @kerry.gould on slack before making changes
 */
export const AnnotateFillPlateModalMachine = setup({
  types: {
    input: {} as AnnotateFillPlateModalInput,
    context: {} as AnnotateFillPlateModalContext,
    events: {} as AnnotateFillPlateModalEvents,
  },
  actions: {
    updateProcedureContext: assign(({context, event}) => {
      assertEvent(event, 'updateProcedureContext');
      const plateDataParameters =
        context.initiatingPlateDesignState?.plateDataParameters ??
        event.procedureContext.plateDataParameters ??
        [];
      return {
        plateDataParameters,
        procedureContext: event.procedureContext,
      };
    }),
    updatePlateMapType: assign(({context, event}) => {
      assertEvent(event, AnnotateFillPlateModalReducerActionType.UPDATE_PLATE_MAP_TYPE);
      if (context.plateMapType === event.plateMapType) {
        return context;
      }
      return {
        ...resetDesignCreateExistingPlateStates(context),
        plateMapType: event.plateMapType,
      };
    }),
    updateInitialPlateMap: assign(({context, event}) => {
      assertEvent(event, AnnotateFillPlateModalReducerActionType.UPDATE_INITIAL_PLATE_MAP);
      const initialSelectedDataParameters = getInitialSelectedDataParameters(event.plateDataParameters);

      let initialPlateMap: PlateMap | null;
      if (event.updatedPlateMapInfo?.plateMap != null) {
        initialPlateMap = produce(event.updatedPlateMapInfo.plateMap, (draft) => {
          draft.wells.forEach((well) => {
            well.dataParameterTags = getInitialDataParameterTags(initialSelectedDataParameters);
            if (event.isInProcedure) {
              well.contents.forEach((content) => {
                content.materialInput = {materialInputId: NO_MATERIAL_INPUT_SENTINEL, error: null};
              });
            }
          });
          draft.dataParameterNamesByVersionId = getDataParameterNamesByVersionId(event.plateDataParameters);
        });
      } else {
        initialPlateMap = null;
      }
      return {
        initialPlateMap,
        containerSchemaId: event.updatedPlateMapInfo?.containerSchemaId ?? null,
      };
    }),

    updateStepKey: assign(({event, context}) => {
      assertEvent(event, AnnotateFillPlateModalReducerActionType.UPDATE_STEP_KEY);

      const modalSteps = ANNOTATE_FILL_PLATE_MODAL_STEPS;
      const newStepIndex = modalSteps.findIndex((step) => step.key === event.stepKey);
      return produce(context, (draft) => {
        draft.stepKey = event.stepKey;
        modalSteps.forEach((step, index) => {
          draft.statusByStepKey[step.key] =
            index < newStepIndex ? StepItemStatus.DONE : StepItemStatus.UNVISITED;
        });
      });
    }),

    createNewPlateMap: assign(({event, context}) => {
      assertEvent(event, AnnotateFillPlateModalReducerActionType.CREATE_NEW_PLATE_MAP);

      if (context.initialPlateMap != null) {
        return {};
      }
      const plateSchema =
        context.plateMapType === PlateMapType.DESIGN
          ? context.plateDesignState.plateSchema
          : context.plateMapType === PlateMapType.RECORD
            ? context.createPlatePaneState.selectedSchema
            : null;
      assert(plateSchema != null, 'Expected plate schema to be selected before CREATE_NEW_PLATE_MAP.', {
        plateMapType: context.plateMapType,
      });
      return {
        initialPlateMap: getEmptyPlateMap(
          plateSchema,
          event.containerSchemaFields,
          event.plateDataParameters
        ),
        containerSchemaId: event.containerSchemaId,
      };
    }),

    updatePlateDefinitionType: assign(({event, context}) => {
      assertEvent(event, AnnotateFillPlateModalReducerActionType.UPDATE_PLATE_DEFINITION_TYPE);
      if (context.plateDefinitionType === event.plateDefinitionType) {
        return context;
      }
      return {
        ...resetDesignCreateExistingPlateStates(context),
        plateDefinitionType: event.plateDefinitionType,
      };
    }),

    updateCreatePlatePaneState: assign(({event, context}) => {
      assertEvent(event, AnnotateFillPlateModalReducerActionType.UPDATE_CREATE_PLATE_PANE_STATE);
      return produce(context, (draft) => {
        draft.createPlatePaneState = event.createPlatePaneState;
        // If a plate map was previously created but the schema changed, clear the plate map.
        if (
          context.initialPlateMap != null &&
          context.createPlatePaneState.selectedSchema?.id !== event.createPlatePaneState.selectedSchema?.id
        ) {
          draft.initialPlateMap = null;
          draft.containerSchemaId = null;
        }
      });
    }),

    updateExistingPlateState: assign(({event, context}) => {
      assertEvent(event, AnnotateFillPlateModalReducerActionType.UPDATE_EXISTING_PLATE_STATE);
      return produce(context, (draft) => {
        draft.existingPlateState = event.existingPlateState;
        if (
          event.existingPlateState.selectedGridId != null &&
          !event.existingPlateState.isValidating &&
          event.existingPlateState.error == null
        ) {
          draft.selectedGrid = StorableStore.getGrid<FullyAccessibleGrid>(
            event.existingPlateState.selectedGridId
          );
        } else if (event.existingPlateState.selectedGridId == null) {
          draft.selectedGrid = null;
        }
      });
    }),

    updatePlateDesignState: assign(({event, context}) => {
      assertEvent(event, AnnotateFillPlateModalReducerActionType.UPDATE_PLATE_DESIGN_STATE);
      return produce(context, (draft) => {
        draft.plateDesignState = event.plateDesignState;
        // If a plate map was previously created but the schema changed, clear the plate map.
        if (
          context.initialPlateMap != null &&
          context.plateDesignState.plateSchema?.id !== event.plateDesignState.plateSchema?.id
        ) {
          draft.initialPlateMap = null;
          draft.containerSchemaId = null;
        }
      });
    }),

    dismissValidationError: assign(({event, context}) => {
      assertEvent(event, AnnotateFillPlateModalReducerActionType.DISMISS_VALIDATION_ERROR);
      return {
        dismissablePlateMapValidation: filterGroupedValidation(
          context.dismissablePlateMapValidation,
          (errorWithUuid) => !event.errorUuids.has(errorWithUuid.uuid)
        ),
      };
    }),

    spawnConcentrationLayerMachine: assign(({spawn}) => {
      const concentrationLayerMachine = spawn(ConcentrationLayerMachine, {
        input: {},
      });

      return {
        concentrationLayerMachine,
      };
    }),
  },
}).createMachine({
  context: ({input}) => {
    const {
      initiatingPlateDesignState,
      procedureContext,
      existingPlateDesignNames,
      studyableProps,
      fileId,
      note,
    } = input;

    const selectedPlateDataParameterIds = initiatingPlateDesignState?.selectedPlateDataParameterIds;

    const shouldAllowPlateDesigns = allowPlateDesigns({fileId, isConfig: isConfiguration(fileId)});
    const shouldAllowPlateRecords = allowPlateRecords({fileId, isConfig: isConfiguration(fileId)});

    let initialPlateMapType = initiatingPlateDesignState?.plateMapType;
    if (initialPlateMapType == null) {
      if (shouldAllowPlateDesigns && !shouldAllowPlateRecords) {
        initialPlateMapType = PlateMapType.DESIGN;
      } else if (!shouldAllowPlateDesigns && shouldAllowPlateRecords) {
        initialPlateMapType = PlateMapType.RECORD;
      }
    }

    const initialPlateMap = initiatingPlateDesignState?.plateMap;
    const initialPlateDesignState = initiatingPlateDesignState?.plateDesignState;
    const plateMapType =
      initiatingPlateDesignState?.plateMapType ??
      (initialPlateDesignState ? PlateMapType.DESIGN : initialPlateMapType) ??
      null;

    const registry = getInitialRegistryForPlateDesign(initiatingPlateDesignState?.plateDesignState);

    const initialWarnings: PlateMapValidation =
      initiatingPlateDesignState?.plateMapStalenessValidation == null
        ? {
            layerValidation: [],
            wellValidation: [],
            fieldValueValidation: [],
            reagentValidation: [],
          }
        : filterValidation(
            initiatingPlateDesignState.plateMapStalenessValidation,
            (error) => error.level === PlateMapErrorLevel.WARNING
          );

    const initialValidationLength =
      initiatingPlateDesignState?.plateMapStalenessValidation != null
        ? getFlattenedValidation(initiatingPlateDesignState.plateMapStalenessValidation).length
        : 0;
    const filteredLength = getFlattenedValidation(initialWarnings).length;
    assert(
      initialValidationLength === filteredLength,
      'Unexpected non-dismissable error in staleness validation response'
    );
    const dismissablePlateMapValidation = getErrorsByLayerAndLevel(initialWarnings);

    const plateDataParameters =
      initiatingPlateDesignState?.plateDataParameters ?? procedureContext.plateDataParameters ?? [];

    return {
      ...DEFAULT_ANNOTATE_FILL_PLATE_MODAL_STATE,
      plateMapType,

      existingPlateState: {
        ...DEFAULT_ANNOTATE_FILL_PLATE_MODAL_STATE.existingPlateState,
        appendStudyIds: input.initialStudyIds ?? [],
      },
      createPlatePaneState: {
        ...DEFAULT_ANNOTATE_FILL_PLATE_MODAL_STATE.createPlatePaneState,
        studyIds: input.initialStudyIds ?? [],
        registry,
        selectedSchema: initialPlateDesignState?.plateSchema ?? null,
      },
      plateDesignState: initialPlateDesignState ?? DEFAULT_PLATE_DESIGN_STATE,
      initialPlateMap: initialPlateMap ?? null,
      containerSchemaId: initialPlateDesignState?.plateSchema?.containerSchemaId ?? null,
      isCreatingPlateRecordFromDesign:
        initialPlateDesignState != null && plateMapType === PlateMapType.RECORD,
      dismissablePlateMapValidation,

      isFromExistingPlateDesign: initialPlateDesignState != null,

      plateDataParameters,
      existingPlateDesignNames,

      selectedPlateDataParameterIds,

      procedureContext,

      fileId,

      initiatingPlateDesignState,

      studyableProps,
      note,

      allowPlateDesigns: shouldAllowPlateDesigns,
      allowPlateRecords: shouldAllowPlateRecords,

      disabledSections: shouldAllowPlateRecords ? [] : [DisplayLayerOptionSection.CONTENTS],
      ...(input.initialState ?? {}),
    };
  },
  on: {
    UPDATE_PLATE_MAP_TYPE: {
      actions: 'updatePlateMapType',
    },
    UPDATE_INITIAL_PLATE_MAP: {
      actions: 'updateInitialPlateMap',
    },

    UPDATE_STEP_KEY: {
      actions: 'updateStepKey',
    },

    CREATE_NEW_PLATE_MAP: {
      actions: 'createNewPlateMap',
    },
    UPDATE_PLATE_DEFINITION_TYPE: {
      actions: 'updatePlateDefinitionType',
    },
    UPDATE_CREATE_PLATE_PANE_STATE: {
      actions: 'updateCreatePlatePaneState',
    },
    UPDATE_EXISTING_PLATE_STATE: {
      actions: 'updateExistingPlateState',
    },
    UPDATE_PLATE_DESIGN_STATE: {
      actions: 'updatePlateDesignState',
    },
    DISMISS_VALIDATION_ERROR: {
      actions: 'dismissValidationError',
    },
    updateProcedureContext: {
      actions: 'updateProcedureContext',
    },
  },
  id: 'AnnotateFillPlate',
  initial: 'idle',
  states: {
    idle: {
      entry: 'spawnConcentrationLayerMachine',
    },
  },
});
